import type { Color, PieceSymbol, Square } from 'chess.js';
import { Chess } from 'chess.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Peca } from './Peca';

export interface SetaTabuleiro {
  de: string;
  para: string;
  /** 'dica' = melhor lance; 'pv' = linha alternativa */
  tipo?: 'dica' | 'pv';
  opacidade?: number;
}

interface TabuleiroProps {
  jogo: Chess;
  orientacao: Color;
  selecionada: Square | null;
  destinos: Array<{ para: Square; captura: boolean }>;
  ultimaJogada?: { de: string; para: string };
  /** Quando true, o próximo lance (ex.: arraste) não anima o voo da peça. */
  pularAnimacao?: boolean;
  desabilitado: boolean;
  mostrarDestinos: boolean;
  setas?: SetaTabuleiro[];
  aoSelecionar: (casa: Square) => void;
  aoMover: (de: Square, para: Square) => void;
}

/** Distância mínima (px) para distinguir toque de arraste. */
const LIMIAR_ARRASTE = 8;
/** Duração base do deslize da peça (ms). */
const DURACAO_ANIM_MS = 320;

function colLinha(casa: string, orientacao: Color): { col: number; row: number } {
  const arquivo = casa.charCodeAt(0) - 97;
  const fileira = Number(casa[1]) - 1;
  const col = orientacao === 'w' ? arquivo : 7 - arquivo;
  const row = orientacao === 'w' ? 7 - fileira : fileira;
  return { col, row };
}

function centroCasa(casa: string, orientacao: Color): { x: number; y: number } {
  const { col, row } = colLinha(casa, orientacao);
  return { x: (col + 0.5) * 12.5, y: (row + 0.5) * 12.5 };
}

function cantoCasaPct(casa: string, orientacao: Color): { left: number; top: number } {
  const { col, row } = colLinha(casa, orientacao);
  return { left: col * 12.5, top: row * 12.5 };
}

function SetaSvg({ de, para, orientacao, tipo = 'dica', opacidade = 0.88 }: SetaTabuleiro & { orientacao: Color }) {
  const a = centroCasa(de, orientacao);
  const b = centroCasa(para, orientacao);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  const encolhe = 1.6;
  const x1 = a.x + ux * encolhe;
  const y1 = a.y + uy * encolhe;
  const x2 = b.x - ux * encolhe;
  const y2 = b.y - uy * encolhe;
  const angulo = (Math.atan2(dy, dx) * 180) / Math.PI;
  const cor = tipo === 'dica' ? 'rgba(199,162,96,.92)' : 'rgba(140,160,190,.72)';

  return (
    <g opacity={opacidade} pointerEvents="none">
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={cor} strokeWidth={tipo === 'dica' ? 1.35 : 1} strokeLinecap="round" />
      <polygon points="-1.1,-1.4 2.4,0 -1.1,1.4" fill={cor} transform={`translate(${x2},${y2}) rotate(${angulo})`} />
    </g>
  );
}

function casaEmPonto(x: number, y: number): Square | null {
  const el = document.elementFromPoint(x, y);
  if (!el) return null;
  const botao = el.closest<HTMLElement>('[data-casa]');
  const casa = botao?.dataset.casa;
  return casa ? (casa as Square) : null;
}

interface ArrasteAtivo {
  casa: Square;
  tipo: PieceSymbol;
  cor: Color;
  pointerId: number;
  inicioX: number;
  inicioY: number;
  /** true após ultrapassar o limiar de movimento */
  movendo: boolean;
  /** tamanho da casa no início do arraste (para o fantasma) */
  tamanho: number;
}

interface PecaEmVoo {
  id: string;
  de: string;
  para: string;
  tipo: PieceSymbol;
  cor: Color;
  left: number;
  top: number;
  emMovimento: boolean;
}

/** Torre que acompanha o rei no roque. */
function torreDoRoque(deRei: string, paraRei: string): { de: string; para: string } | null {
  if (deRei[1] !== paraRei[1]) return null;
  const df = paraRei.charCodeAt(0) - deRei.charCodeAt(0);
  if (Math.abs(df) !== 2) return null;
  const fileira = deRei[1];
  // O-O: rei e→g, torre h→f | O-O-O: rei e→c, torre a→d
  if (df > 0) return { de: `h${fileira}`, para: `f${fileira}` };
  return { de: `a${fileira}`, para: `d${fileira}` };
}

function prefereMovimentoReduzido(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export function Tabuleiro({
  jogo,
  orientacao,
  selecionada,
  destinos,
  ultimaJogada,
  pularAnimacao = false,
  desabilitado,
  mostrarDestinos,
  setas = [],
  aoSelecionar,
  aoMover
}: TabuleiroProps) {
  const [arrastando, setArrastando] = useState<Square | null>(null);
  const [fantasma, setFantasma] = useState<{
    x: number;
    y: number;
    tipo: PieceSymbol;
    cor: Color;
    tamanho: number;
  } | null>(null);
  const arrasteRef = useRef<ArrasteAtivo | null>(null);
  /** Após soltar um arraste, ignora o click sintético do browser. */
  const ignorarClickRef = useRef(false);
  const ultimaChaveRef = useRef<string | null>(null);
  const [voos, setVoos] = useState<PecaEmVoo[]>([]);
  /** Casas de destino com peça oculta enquanto anima. */
  const ocultasDestino = useMemo(() => new Set(voos.map((v) => v.para)), [voos]);

  const casas = useMemo(() => {
    const arquivos = orientacao === 'w' ? ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] : ['h', 'g', 'f', 'e', 'd', 'c', 'b', 'a'];
    const fileiras = orientacao === 'w' ? ['8', '7', '6', '5', '4', '3', '2', '1'] : ['1', '2', '3', '4', '5', '6', '7', '8'];
    return fileiras.flatMap((fileira) => arquivos.map((arquivo) => `${arquivo}${fileira}` as Square));
  }, [orientacao]);
  const destinosMap = new Map(destinos.map((item) => [item.para, item]));
  const reiEmXeque = jogo.inCheck() ? jogo.findPiece({ type: 'k', color: jogo.turn() })[0] : undefined;

  // Anima o deslize da peça quando a última jogada muda (CPU, clique, online…).
  useEffect(() => {
    if (!ultimaJogada) return;
    const chave = `${ultimaJogada.de}${ultimaJogada.para}`;
    if (ultimaChaveRef.current === chave) return;
    ultimaChaveRef.current = chave;

    if (pularAnimacao || prefereMovimentoReduzido()) {
      setVoos([]);
      return;
    }

    const pecaDestino = jogo.get(ultimaJogada.para as Square);
    if (!pecaDestino) return;

    const pecas: Array<{ de: string; para: string; tipo: PieceSymbol; cor: Color }> = [
      {
        de: ultimaJogada.de,
        para: ultimaJogada.para,
        tipo: pecaDestino.type as PieceSymbol,
        cor: pecaDestino.color
      }
    ];

    // Roque: anima a torre junto com o rei.
    if (pecaDestino.type === 'k') {
      const torre = torreDoRoque(ultimaJogada.de, ultimaJogada.para);
      if (torre) {
        const t = jogo.get(torre.para as Square);
        if (t?.type === 'r') {
          pecas.push({ de: torre.de, para: torre.para, tipo: 'r', cor: t.color });
        }
      }
    }

    const iniciais: PecaEmVoo[] = pecas.map((p, i) => {
      const pos = cantoCasaPct(p.de, orientacao);
      return {
        id: `${chave}-${i}`,
        de: p.de,
        para: p.para,
        tipo: p.tipo,
        cor: p.cor,
        left: pos.left,
        top: pos.top,
        emMovimento: false
      };
    });
    setVoos(iniciais);

    let cancelado = false;
    let timerFim: number | undefined;
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelado) return;
        setVoos((atuais) =>
          atuais.map((v) => {
            const dest = cantoCasaPct(v.para, orientacao);
            return { ...v, left: dest.left, top: dest.top, emMovimento: true };
          })
        );
        timerFim = window.setTimeout(() => {
          if (!cancelado) setVoos([]);
        }, DURACAO_ANIM_MS + 40);
      });
    });

    return () => {
      cancelado = true;
      cancelAnimationFrame(raf1);
      if (timerFim) window.clearTimeout(timerFim);
    };
  }, [ultimaJogada, jogo, orientacao, pularAnimacao]);

  // Ao virar o tabuleiro no meio da animação, encerra o voo.
  useEffect(() => {
    setVoos([]);
  }, [orientacao]);

  const limparArraste = useCallback(() => {
    arrasteRef.current = null;
    setArrastando(null);
    setFantasma(null);
  }, []);

  const iniciarArraste = useCallback(
    (evento: React.PointerEvent, casa: Square, tipo: PieceSymbol, cor: Color) => {
      if (desabilitado || cor !== jogo.turn()) return;
      // Só botão primário / toque; ignora caneta secundária e clique direito.
      if (evento.pointerType === 'mouse' && evento.button !== 0) return;

      const alvo = evento.currentTarget as HTMLElement;
      const casaEl = alvo.closest('.casa') as HTMLElement | null;
      const tamanho = casaEl?.offsetWidth ?? alvo.offsetWidth ?? 48;

      arrasteRef.current = {
        casa,
        tipo,
        cor,
        pointerId: evento.pointerId,
        inicioX: evento.clientX,
        inicioY: evento.clientY,
        movendo: false,
        tamanho
      };

      try {
        alvo.setPointerCapture(evento.pointerId);
      } catch {
        /* alguns browsers recusam capture se o pointer já saiu */
      }
    },
    [desabilitado, jogo]
  );

  const aoMoverPonteiro = useCallback(
    (evento: React.PointerEvent) => {
      const arraste = arrasteRef.current;
      if (!arraste || arraste.pointerId !== evento.pointerId) return;

      const dx = evento.clientX - arraste.inicioX;
      const dy = evento.clientY - arraste.inicioY;

      if (!arraste.movendo) {
        if (Math.hypot(dx, dy) < LIMIAR_ARRASTE) return;
        arraste.movendo = true;
        setArrastando(arraste.casa);
        // Destaca destinos legais enquanto arrasta.
        aoSelecionar(arraste.casa);
      }

      evento.preventDefault();
      setFantasma({
        x: evento.clientX,
        y: evento.clientY,
        tipo: arraste.tipo,
        cor: arraste.cor,
        tamanho: arraste.tamanho
      });
    },
    [aoSelecionar]
  );

  const aoSoltarPonteiro = useCallback(
    (evento: React.PointerEvent) => {
      const arraste = arrasteRef.current;
      if (!arraste || arraste.pointerId !== evento.pointerId) return;

      const alvo = evento.currentTarget as HTMLElement;
      try {
        if (alvo.hasPointerCapture?.(evento.pointerId)) {
          alvo.releasePointerCapture(evento.pointerId);
        }
      } catch {
        /* ignore */
      }

      if (arraste.movendo) {
        // Evita o click sintético após touchend/mouseup do arraste.
        ignorarClickRef.current = true;
        const destino = casaEmPonto(evento.clientX, evento.clientY);
        if (destino && destino !== arraste.casa && !desabilitado) {
          aoMover(arraste.casa, destino);
        }
        limparArraste();
        return;
      }

      // Toque sem arraste: deixa o onClick da casa tratar a seleção.
      limparArraste();
    },
    [aoMover, desabilitado, limparArraste]
  );

  const aoCancelarPonteiro = useCallback(
    (evento: React.PointerEvent) => {
      const arraste = arrasteRef.current;
      if (!arraste || arraste.pointerId !== evento.pointerId) return;
      if (arraste.movendo) ignorarClickRef.current = true;
      limparArraste();
    },
    [limparArraste]
  );

  // Se o tabuleiro for desabilitado no meio do arraste (ex.: turno do motor), limpa.
  useEffect(() => {
    if (desabilitado && arrasteRef.current) limparArraste();
  }, [desabilitado, limparArraste]);

  function clicarCasa(casa: Square) {
    if (ignorarClickRef.current) {
      ignorarClickRef.current = false;
      return;
    }
    if (!desabilitado) aoSelecionar(casa);
  }

  return (
    <div className="moldura-tabuleiro">
      <div className="friso-tabuleiro"><span>XADREZ</span><i /></div>
      {/*
        Importante: o grid 8×8 só pode ter as 64 casas.
        Setas e peças em voo ficam em overlay absoluto FORA do grid.
      */}
      <div className="superficie-tabuleiro">
        <div
          className={`tabuleiro ${desabilitado ? 'tabuleiro-bloqueado' : ''} ${arrastando ? 'tabuleiro-arrastando' : ''}`}
          role="grid"
          aria-label="Tabuleiro de xadrez"
        >
          {casas.map((casa, indice) => {
            const peca = jogo.get(casa);
            const arquivo = casa.charCodeAt(0) - 97;
            const fileira = Number(casa[1]);
            const clara = (arquivo + fileira) % 2 === 1;
            const destino = destinosMap.get(casa);
            const ultima = ultimaJogada?.de === casa || ultimaJogada?.para === casa;
            const selecionadaAqui = selecionada === casa;
            const emXeque = reiEmXeque === casa;
            const linhaVisual = Math.floor(indice / 8);
            const colunaVisual = indice % 8;
            const podeArrastar = Boolean(peca && !desabilitado && peca.color === jogo.turn());
            const ocultarPeca = Boolean(peca && ocultasDestino.has(casa));
            return (
              <button
                type="button"
                role="gridcell"
                aria-label={`${casa}${peca ? `, ${peca.color === 'w' ? 'peça clara' : 'peça escura'}` : ''}`}
                key={casa}
                data-casa={casa}
                className={`casa ${clara ? 'casa-clara' : 'casa-escura'} ${ultima ? 'casa-ultima' : ''} ${selecionadaAqui ? 'casa-selecionada' : ''} ${emXeque ? 'casa-xeque' : ''}`}
                onClick={() => clicarCasa(casa)}
              >
                {colunaVisual === 0 && <span className="coordenada-fileira">{casa[1]}</span>}
                {linhaVisual === 7 && <span className="coordenada-arquivo">{casa[0]}</span>}
                {mostrarDestinos && destino && <span className={destino.captura ? 'destino-captura' : 'destino-livre'} />}
                {peca && !ocultarPeca && (
                  <span
                    className={`envoltoria-peca ${arrastando === casa ? 'peca-arrastada' : ''} ${podeArrastar ? 'peca-arrastavel' : ''}`}
                    onPointerDown={(e) => iniciarArraste(e, casa, peca.type as PieceSymbol, peca.color)}
                    onPointerMove={aoMoverPonteiro}
                    onPointerUp={aoSoltarPonteiro}
                    onPointerCancel={aoCancelarPonteiro}
                    onLostPointerCapture={aoCancelarPonteiro}
                    // Impede seleção de texto / gestos nativos durante o toque.
                    style={{ touchAction: 'none' }}
                  >
                    <Peca tipo={peca.type as PieceSymbol} cor={peca.color} />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {voos.length > 0 && (
          <div className="camada-animacao-lances" aria-hidden="true">
            {voos.map((v) => (
              <div
                key={v.id}
                className={`peca-em-voo ${v.emMovimento ? 'peca-em-voo-ativa' : ''}`}
                style={{
                  left: `${v.left}%`,
                  top: `${v.top}%`,
                  transitionDuration: v.emMovimento ? `${DURACAO_ANIM_MS}ms` : '0ms'
                }}
              >
                <Peca tipo={v.tipo} cor={v.cor} />
              </div>
            ))}
          </div>
        )}
        {setas.length > 0 && (
          <svg className="camada-setas" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {setas.map((s) => (
              <SetaSvg key={`${s.de}${s.para}${s.tipo ?? 'dica'}`} {...s} orientacao={orientacao} />
            ))}
          </svg>
        )}
      </div>
      <div className="assinatura-tabuleiro"><i /><span>ARTESANATO DIGITAL</span><i /></div>

      {fantasma && (
        <div
          className="peca-fantasma"
          aria-hidden="true"
          style={{
            left: fantasma.x,
            top: fantasma.y,
            width: fantasma.tamanho * 0.92,
            height: fantasma.tamanho * 0.92
          }}
        >
          <Peca tipo={fantasma.tipo} cor={fantasma.cor} />
        </div>
      )}
    </div>
  );
}
